// @ts-check
// Editor do conteúdo do e-mail (spec/email.md seção 19.2), usado pela página do modelo e, na onda 2, pelo passo
// Conteúdo da campanha (frente 2A): assunto e texto de prévia com o seletor de variáveis, os erros e avisos da
// compilação (com "Ir para o bloco") e o editor do modo (blocos, texto simples ou HTML). Controlado: devolve o valor
// inteiro a cada mudança; quem usa guarda a pilha de desfazer (email-document-store.js) e grava.
import { html } from 'htm/preact';
import { useMemo, useRef, useState } from 'preact/hooks';
import { BlockEditor } from './block-editor/block-editor.js';
import { RichTextMode } from './rich-text-mode.js';
import { HtmlMode } from './html-mode.js';
import { VariablePicker, useEmailVariableCatalog } from './variable-picker.js';
import { browserBaseUrl, renderLocalPreview } from './local-preview.js';
import { LightIconButton } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../../shared/email-design/email-document.js').EmailEditorMode} EmailEditorMode
 * @typedef {import('../../data/email-templates.js').EmailMarketingSettings} EmailMarketingSettings
 * @typedef {{ editorMode: EmailEditorMode, document: EmailDocument, subject: string, preheader: string, purpose: 'MARKETING' | 'SERVICE' }} EmailContentValue
 * @typedef {import('./block-editor/block-canvas.js').EditorSelection} EditorSelection
 */

const MAXIMUM_LINE_LENGTH = 200;
const LONG_SUBJECT_LENGTH = 70;

/**
 * Prévia local e resultado da compilação do valor do editor (recalculados só quando algo muda).
 * @param {EmailContentValue} value
 * @param {{ settings: EmailMarketingSettings | null, institutionName: string, logoUrl: string | null }} options
 */
export function useLocalEmailPreview(value, options) {
  return useMemo(() => renderLocalPreview(value.document, {
    subject: value.subject,
    preheader: value.preheader,
    purpose: value.purpose,
    settings: options.settings,
    institutionName: options.institutionName,
    logoUrl: options.logoUrl,
    baseUrl: browserBaseUrl(),
  }), [value.document, value.subject, value.preheader, value.purpose, options.settings, options.institutionName, options.logoUrl]);
}

/**
 * Linha de assunto ou de texto de prévia, com o seletor de variáveis.
 * @param {{ id: string, label: string, value: string, onChange: (value: string) => void, hint?: string, readOnly: boolean,
 *   catalog: import('../../data/email-templates.js').EmailVariableCatalog | null, placeholder?: string, warning?: string | null }} props
 */
function EmailLineField({ id, label, value, onChange, hint, readOnly, catalog, placeholder, warning }) {
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const anchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  /** @param {string} expression */
  const insert = (expression) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${expression}${value.slice(end)}`.slice(0, MAXIMUM_LINE_LENGTH);
    onChange(next);
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + expression.length, start + expression.length);
    });
  };
  return html`<div class="email-line-field">
    <label class="email-line-label" for=${id}>${label}</label>
    <div class="email-line-input" ref=${anchorRef}>
      <input ref=${inputRef} id=${id} class="text-input" value=${value} maxLength=${MAXIMUM_LINE_LENGTH} readOnly=${readOnly} placeholder=${placeholder}
        onInput=${(/** @type {Event} */ event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      <span class="email-line-counter" aria-hidden="true">${value.length}/${MAXIMUM_LINE_LENGTH}</span>
      ${readOnly ? null : html`<${LightIconButton} icon="IconBraces" ariaLabel=${t('emailTemplates.variables.title')} onClick=${() => setIsPickerOpen((open) => !open)} />`}
    </div>
    ${warning ? html`<span class="input-hint" data-danger="true">${warning}</span>` : hint ? html`<span class="input-hint">${hint}</span>` : null}
    <${VariablePicker} anchorRef=${anchorRef} isOpen=${isPickerOpen} onClose=${() => setIsPickerOpen(false)} catalog=${catalog} placement="bottom-end"
      excludePaths=${['unsubscribeUrl']} onSelect=${(/** @type {string} */ expression) => insert(expression)} />
  </div>`;
}

/**
 * Erros e avisos da compilação, com "Ir para o bloco".
 * @param {{ errors: Array<{ code: string, blockId?: string }>, warnings: Array<{ code: string, blockId?: string }>, onGoToBlock?: (blockId: string) => void }} props
 */
export function EmailProblems({ errors, warnings, onGoToBlock }) {
  const [isOpen, setIsOpen] = useState(false);
  if (errors.length === 0 && warnings.length === 0) return null;
  const summary = [
    errors.length ? t('emailTemplates.editor.errorsCount', { count: errors.length }) : '',
    warnings.length ? t('emailTemplates.editor.warningsCount', { count: warnings.length }) : '',
  ].filter(Boolean).join(', ');
  return html`<div class="email-problems" data-severity=${errors.length ? 'error' : 'warning'}>
    <button type="button" class="email-problems-summary" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => setIsOpen((open) => !open)}>
      <${Icon} name=${errors.length ? 'IconAlertTriangle' : 'IconInfoCircle'} size=${14} />
      <span>${summary}</span>
      <${Icon} name=${isOpen ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />
    </button>
    ${isOpen
      ? html`<ul class="email-problems-list">
          ${[...errors.map((problem) => ({ ...problem, severity: 'error' })), ...warnings.map((problem) => ({ ...problem, severity: 'warning' }))].map((problem) => html`<li data-severity=${problem.severity}>
            <span>${t(`emailTemplates.problems.${problem.code}`)}</span>
            ${problem.blockId && onGoToBlock
              ? html`<button type="button" class="email-link-button" onClick=${() => onGoToBlock(/** @type {string} */ (problem.blockId))}>${t('emailTemplates.editor.goToBlock')}</button>`
              : null}
          </li>`)}
        </ul>`
      : null}
  </div>`;
}

/**
 * @param {{ value: EmailContentValue, onChange: (value: EmailContentValue, options?: { coalesceKey?: string }) => void,
 *   context: 'TEMPLATE' | 'CAMPAIGN', readOnly?: boolean, brandColor: string, logoUrl: string | null,
 *   settings?: EmailMarketingSettings | null, institutionName?: string, onSendTest?: () => void, onSaveAsTemplate?: () => void,
 *   extraProblems?: Array<{ code: string, blockId?: string }> }} props
 *   extraProblems: avisos do servidor que o navegador não calcula (variável oculta, imagem pesada)
 */
export function EmailContentEditor({ value, onChange, context, readOnly = false, brandColor, logoUrl, settings = null, institutionName = '', extraProblems = [] }) {
  const catalog = useEmailVariableCatalog();
  const [selection, setSelection] = useState(/** @type {EditorSelection} */ (null));
  const valueRef = useRef(value);
  valueRef.current = value;
  const preview = useLocalEmailPreview(value, { settings, institutionName, logoUrl });
  const compiled = preview.compiled;

  /** @param {EmailDocument} document @param {{ coalesceKey?: string }} [options] */
  const changeDocument = (document, options) => onChange({ ...valueRef.current, document }, options);
  /** @param {string} blockId */
  const goToBlock = (blockId) => {
    setSelection({ kind: 'BLOCK', id: blockId });
    requestAnimationFrame(() => {
      const element = /** @type {HTMLElement | null} */ (window.document.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`));
      element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      element?.focus({ preventScroll: true });
    });
  };
  const warnings = [...compiled.warnings, ...extraProblems.filter((problem) => !compiled.warnings.some((warning) => warning.code === problem.code))];

  return html`<div class="email-content-editor" data-mode=${value.editorMode} data-context=${context} data-readonly=${readOnly ? 'true' : undefined}>
    <div class="email-lines">
      <${EmailLineField} id="email-subject" label=${t('emailTemplates.editor.subject')} value=${value.subject} readOnly=${readOnly} catalog=${catalog}
        placeholder=${t('emailTemplates.editor.subjectPlaceholder')}
        warning=${value.subject.trim() ? null : t('emailTemplates.editor.subjectMissing')}
        hint=${value.subject.length > LONG_SUBJECT_LENGTH ? t('emailTemplates.editor.subjectLong') : undefined}
        onChange=${(/** @type {string} */ subject) => onChange({ ...valueRef.current, subject }, { coalesceKey: 'subject' })} />
      <${EmailLineField} id="email-preheader" label=${t('emailTemplates.editor.preheader')} value=${value.preheader} readOnly=${readOnly} catalog=${catalog}
        hint=${t('emailTemplates.editor.preheaderHint')}
        onChange=${(/** @type {string} */ preheader) => onChange({ ...valueRef.current, preheader }, { coalesceKey: 'preheader' })} />
    </div>
    <${EmailProblems} errors=${compiled.errors} warnings=${warnings} onGoToBlock=${value.editorMode === 'BLOCKS' ? goToBlock : undefined} />
    <div class="email-mode-area">
      ${value.editorMode === 'BLOCKS'
        ? html`<${BlockEditor} document=${value.document} onChange=${changeDocument} readOnly=${readOnly} brandColor=${brandColor} logoUrl=${logoUrl}
            baseUrl=${browserBaseUrl()} catalog=${catalog} problems=${compiled} selection=${selection} onSelectionChange=${setSelection} />`
        : value.editorMode === 'RICH_TEXT'
          ? html`<${RichTextMode} document=${value.document} onChange=${changeDocument} readOnly=${readOnly} brandColor=${brandColor} logoUrl=${logoUrl} catalog=${catalog} />`
          : html`<${HtmlMode} document=${value.document} onChange=${changeDocument} readOnly=${readOnly} catalog=${catalog} estimatedHeight=${compiled.estimatedHeight}
              previewHtml=${(/** @type {EmailDocument} */ document) => renderLocalPreview(document, {
                subject: valueRef.current.subject, preheader: valueRef.current.preheader, purpose: valueRef.current.purpose, settings, institutionName, logoUrl,
                baseUrl: browserBaseUrl(),
              }).html} />`}
    </div>
  </div>`;
}
